import {
  AlertTriangle,
  LineChart,
  Users,
  Settings,
  ShieldCheck,
  Building2,
  Layers,
  GitBranch,
  Workflow,
  BarChart3,
  Database,
  Repeat,
  Package,
  CalendarClock,
  Mail,
  Star,
  Bot,
  CreditCard,
  LayoutGrid,
  Plug,
  FileSearch,
  ClipboardList,
  Wrench,
  CheckCircle2,
  Boxes,
  Network,
  GitMerge,
  TrendingUp,
  PieChart,
  Webhook,
  Building,
  Code2,
  Server,
  RefreshCw,
  Cpu,
  DollarSign,
  Zap,
} from "lucide-react";
import type { PlatformConfig } from "./service-platform-config";

/**
 * n8n config — inherits the exact visual system, section order, and CTA
 * pattern of the GoHighLevel reference page, with n8n-specific copy,
 * screenshots, capabilities, symptoms, and FAQs.
 */
export const n8nConfig: PlatformConfig = {
  platformName: "n8n",
  eyebrow: "N8N WORKFLOW ARCHITECT",
  ghostWord: "n8n",
  architectureGhostWord: "ORCHESTRATION",

  heroHeadlineNavy: "Kill Your Zapier Tax.",
  heroHeadlineBlue: "Build Self-Hosted n8n.",
  heroAuthorRole: "n8n Workflow Architect",
  heroPara1:
    "I engineer self-hosted, code-level n8n orchestrations with custom JavaScript nodes, automated error healing, and webhook-first routing for agencies that refuse to babysit brittle Zapier stacks. 300+ workflows architected. Zero account managers between us.",
  heroPara2:
    "Unlimited execution volume. No per-task bills. Production-grade automation that scales.",
  heroCtaLabel: "Book a Workflow Audit",
  heroVideoSrc:
    "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6a4e898c8b929b11f2e756ab.mp4",
  heroVideoPoster:
    "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6abbae84735e4fa2a0085f40.webp",
  heroVideoAlt: "Video walkthrough of an n8n workflow architecture build",
  heroTrustHeader: "Trusted by High-Growth Agencies & Enterprises",

  painEyebrow: "IS THIS YOUR n8n?",
  painHeadlineNavy: "The Symptoms of a",
  painHeadlineBlue: "Workflow Built Wrong.",
  painSub:
    "If two or more of these feel familiar, the issue usually isn't another node. It's the architecture underneath it.",
  symptoms: [
    {
      icon: AlertTriangle,
      title: "Workflows fail silently at 3 AM",
      desc: "A webhook drops and no one knows until a client complains, no retry, no fallback, no alert. Leads vanish into a black hole.",
    },
    {
      icon: LineChart,
      title: "No visibility into execution health",
      desc: "You can't tell which workflows are healthy, which are burning memory, or which haven't fired in weeks.",
    },
    {
      icon: Users,
      title: "Handoffs are 14 manual steps",
      desc: "Data moves between platforms through a manual copy-paste chain, and someone always forgets one.",
    },
    {
      icon: Settings,
      title: "No error handling or retry logic",
      desc: "A single 5xx outage drops the whole chain. No Error Trigger node, no rollback, no dead-letter queue, just lost data.",
    },
    {
      icon: ShieldCheck,
      title: "Three 'experts' who were task-takers",
      desc: "You've hired people who built single linear flows one at a time instead of architecting fault-tolerant orchestrations.",
    },
    {
      icon: Building2,
      title: "Growth outpaces your workflows",
      desc: "Your business is scaling, but your n8n architecture can't keep up with the data volume and complexity.",
    },
  ],
  painCtaLabel: "Get a Workflow Audit",
  painCtaCopy: "If 2+ symptoms hit, let's audit the architecture underneath.",

  archEyebrow: "WHAT I BUILD",
  archHeadlineNavy: "Production-Grade",
  archHeadlineBlue: "n8n Orchestration.",
  archSub:
    "the self-hosted, code-level workflow infrastructure that scales across hundreds of executions without breaking.",
  archSubEmphasis: "Not configuration. Engineering",
  archProofShots: [
    {
      label: "Multi-Branch Error-Healing Router",
      tag: "01 • ROUTING",
      desc: "Conditional branching with Error Trigger nodes, retry backoff, and fallback routes on every critical path.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/d58352c3-1bdf-4727-aadf-eb65e4051977.png",
    },
    {
      label: "Webhook-First Trigger Engine",
      tag: "02 • WEBHOOKS",
      desc: "Instant webhook listeners replacing wasteful polling — real-time triggers with zero task burn.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/245c1411-0d59-49de-868a-b734105d5dae.png",
    },
    {
      label: "Bi-Directional CRM Sync",
      tag: "03 • DATA SYNC",
      desc: "Custom fields, stage changes, and owner reassignments mirrored across platforms in real-time via REST nodes.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/4d74ac9c-4b0d-401d-a9ed-4560b8107502.png",
    },
    {
      label: "Execution Health Dashboard",
      tag: "04 • MONITORING",
      desc: "Real-time execution telemetry, failure alerts, and Slack notifications the moment a workflow breaks.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/19ffa953-83ba-4397-88a9-3fd2520e7142.png",
    },
  ],
  archUnderneathLabel: "THE ARCHITECTURE UNDERNEATH",
  archUnderHeadlineNavy: "Six systems that",
  archUnderHeadlineBlue: "make the whole stack scale.",
  capabilities: [
    {
      icon: GitBranch,
      title: "Multi-Branch Error-Healing Routers",
      desc: "Conditional routing with Error Trigger nodes, exponential retry backoff, and fallback routes so temporary outages never drop leads.",
    },
    {
      icon: Code2,
      title: "Custom JavaScript Code Nodes",
      desc: "Deploy custom JS inside any node for complex transforms, regex validation, and logic that no-code tools simply can't express.",
    },
    {
      icon: Server,
      title: "Self-Hosted Data Privacy",
      desc: "Run n8n on your own infrastructure for full data sovereignty, unlimited execution volume, and a flat monthly cost — no per-task bills.",
    },
    {
      icon: Database,
      title: "Bi-Directional CRM Syncing",
      desc: "Keep custom fields, stage changes, revenue numbers, and owner reassignments mirrored across platforms in real-time via REST nodes.",
    },
    {
      icon: RefreshCw,
      title: "Payload Validation & Normalization",
      desc: "Cleanse phone numbers (E.164), normalize names, validate UTM params, and reject malformed data before CRM entry.",
    },
    {
      icon: Bot,
      title: "AI Agent & LLM Orchestration",
      desc: "Wire Vapi voice AI, OpenAI, and Claude into workflows — transcribe calls, summarize notes, and auto-update deal health tags.",
    },
  ],
  archConclusionText: "Self-hosted. Code-level. Built for production.",
  archConclusionHighlight: "Built for production.",
  archShotAltSuffix: "n8n workflow architecture screenshot",

  costEyebrow: "The Real Cost",
  costHeadlineNavy: "What Broken n8n Workflows",
  costHeadlineBlue: "Actually Cost You.",
  costs: [
    {
      stat: "Silent failures",
      desc: "workflows break at 3 AM with no alert — leads disappear into a black hole and no one knows until a client complains.",
    },
    {
      stat: "8+ hours/week",
      desc: "spent manually re-running failed executions and patching fragile flows — over $25K/year of your time gone.",
    },
    {
      stat: "Zero audit trail",
      desc: "when a workflow fails, no one knows why it broke or which records slipped through — no dead-letter queue, no replay.",
    },
  ],
  costConclusion:
    "Every week you wait, the failure debt compounds. An architect fixes the root — not the symptom.",

  processEyebrow: "The Build Process",
  processHeadlineNavy: "How I Architect Your",
  processHeadlineBlue: "n8n Workflows.",
  processSub:
    "A 4-phase process from audit to scale. No black boxes — you approve before I touch production.",
  processSteps: [
    {
      title: "Workflow Audit",
      timeline: "3–5 days",
      desc: "I map every active workflow, identify silent failures, brittle linear chains, and missing error handling. Delivered as a written report.",
    },
    {
      title: "Architecture Design",
      timeline: "5–10 days",
      desc: "Webhook-first routing blueprint, Error Trigger directives, payload validation schema, and sync map — designed before any build. You approve before I touch production.",
    },
    {
      title: "Build & Deploy",
      timeline: "10–20 days",
      desc: "Multi-branch routers, custom JS nodes, bi-directional sync, retry logic, and Slack alerting. Version-controlled and tested against your real data shapes.",
    },
    {
      title: "Scale & Maintain",
      timeline: "Ongoing",
      desc: "Execution monitoring, workflow updates, new integrations, and orchestration builds as you grow. A documented learning loop, not a black box.",
    },
  ],
  processCtaCopy: "Stop the bleed. Start with a free workflow audit.",
  processCtaLabel: "Start the Audit",

  migrationEyebrow: "System Rescue & Migration",
  migrationHeadlineNavy: "Inherit a Broken Build?",
  migrationHeadlineBlue: "I Audit Before I Touch It.",
  migrationSub:
    "Most of my engagements start with a tangle of brittle Zapier flows or half-built n8n workflows another freelancer left behind. Here's how I take it over without anything breaking.",
  migrationSteps: [
    {
      icon: FileSearch,
      title: "Map",
      desc: "I document every workflow, webhook, node, and data flow in your current setup before touching anything. No surprises, no silent deletions.",
    },
    {
      icon: ClipboardList,
      title: "Document",
      desc: "A written architecture report of what exists, what's broken, and what must be preserved — so you own the map of your own automation.",
    },
    {
      icon: Wrench,
      title: "Rebuild",
      desc: "I engineer the corrected architecture in a controlled staging environment — version-controlled and tested against your real data shapes.",
    },
    {
      icon: CheckCircle2,
      title: "Verify",
      desc: "Migration completes only after a field-by-field verification pass. Zero data loss is the standard, not a hope.",
    },
  ],
  migrationConclusionPre:
    "Zero data loss isn't a promise. It's a 4-step process that starts with",
  migrationConclusionBlue: "documentation, not deletion.",
  migrationCtaCopy:
    "Inheriting a broken workflow stack? I audit before I touch anything.",
  migrationCtaLabel: "Audit My Workflows",

  testimonialsEyebrow: "Client Results",
  testimonialsHeadlineNavy: "What Happens After",
  testimonialsHeadlineBlue: "the Architecture.",
  testimonials: [
    {
      name: "David L.",
      title: "VP Operations, SaaS Company",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/ea773865-76e8-4fcf-a408-ce29bf6cfc65.png",
      text: "Lead response went from hours to seconds. We booked 40% more demos in the first month because no lead went cold. Highest ROI hire this year.",
      metric: "40%",
      metricLabel: "MORE DEMOS BOOKED",
    },
    {
      name: "Kevin P.",
      title: "Sales Director, Insurance Brokerage",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/1da6c5ea-e593-4890-934a-b7da49e32c41.png",
      text: "Our Zapier bill dropped 60% after migrating to self-hosted n8n with webhook-driven routers. Zero silent failures since launch. Closed-won rate up 23% in one quarter.",
      metric: "60%",
      metricLabel: "AUTOMATION COST CUT",
    },
    {
      name: "Maria F.",
      title: "CEO, E-commerce Brand",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/e44983c6-a240-4684-a595-95a725bd8c95.png",
      text: "Decision velocity tripled. He is the architect behind our scaling from $200K to $600K monthly revenue. The workflows he built still run without him.",
      metric: "$200K → $600K",
      metricLabel: "MONTHLY REVENUE SCALED",
    },
  ],
  testimonialsCtaCopy: "Be the next team running clean workflow architecture.",
  testimonialsCtaLabel: "Book a call",

  buildsEyebrow: "What I Build Inside n8n",
  buildsHeadlineNavy: "Every Layer of the",
  buildsHeadlineBlue: "Workflow System.",
  buildsSub:
    "From webhook orchestration to custom code nodes — the full build surface I architect and own end-to-end.",
  builds: [
    { icon: GitBranch, label: "Multi-Branch Routers" },
    { icon: Webhook, label: "Webhook Trigger Nodes" },
    { icon: ShieldCheck, label: "Error Trigger & Retries" },
    { icon: Database, label: "Bi-Directional CRM Sync" },
    { icon: Code2, label: "Custom JavaScript Nodes" },
    { icon: RefreshCw, label: "Payload Validation" },
    { icon: Server, label: "Self-Hosted Deployment" },
    { icon: Workflow, label: "Sub-Workflow Orchestration" },
    { icon: Zap, label: "Polling-to-Webhook Migration" },
    { icon: BarChart3, label: "Execution Dashboards" },
    { icon: Bot, label: "AI Agent Wiring" },
    { icon: LayoutGrid, label: "Multi-Platform Routing" },
  ],
  buildsIntegrationsLabel: "Platforms I Wire Into n8n",
  integrations: [
    "n8n (self-hosted)",
    "GoHighLevel",
    "HubSpot",
    "Close CRM",
    "Zapier",
    "Make.com",
    "Stripe",
    "Slack",
    "Google Sheets",
    "Airtable",
    "Notion",
    "ChatGPT / OpenAI",
    "Claude",
    "Vapi Voice AI",
    "Twilio",
    "PostgreSQL",
  ],
  buildsCtaCopy: "Tell me which layers you need architected.",
  buildsCtaLabel: "Book a call",

  snapshotsEyebrow: "Architecture Library",
  snapshotsHeadlineNavy: "Ready-to-Deploy",
  snapshotsHeadlineBlue: "Workflow Blueprints.",
  snapshotsSub:
    "A preview of the version-controlled n8n architectures I engineer and deploy — not templates copied from a marketplace.",
  snapshots: [
    {
      name: "Error-Healing Router Blueprint",
      tag: "Reliability",
      desc: "Error Trigger nodes, exponential retry backoff, fallback routes, and instant Slack alerts on any failure — no silent drops.",
    },
    {
      name: "Lead-to-CRM Sync Blueprint",
      tag: "Data Sync",
      desc: "Bi-directional mirror between your lead source and CRM — instant webhook triggers, payload validation, and dedup logic wired in.",
    },
    {
      name: "AI Call Summary Blueprint",
      tag: "AI Orchestration",
      desc: "Vapi voice AI → n8n → LLM transcription → CRM notes — calls summarized and deal health tags auto-updated, no manual note-taking.",
    },
    {
      name: "Self-Hosted Starter Blueprint",
      tag: "Infrastructure",
      desc: "Production n8n deployment with Docker, environment secrets, execution logging, and health monitoring — ready for unlimited volume.",
    },
  ],
  snapshotsCtaCopy: "Get a workflow architecture engineered for scale.",
  snapshotsCtaLabel: "Engineer My Workflow",

  industriesEyebrow: "Industries I Build For",
  industriesHeadlineNavy: "Architecture Tuned to",
  industriesHeadlineBlue: "Your Business Model.",
  industriesSub:
    "The same engineering principles, adapted to the way each business model actually moves data across platforms.",
  industries: [
    {
      name: "Marketing Agencies",
      builds: [
        "Lead-to-CRM bi-directional sync",
        "Multi-client workflow routing",
        "Roll-up reporting across sub-accounts",
      ],
    },
    {
      name: "E-commerce Brands",
      builds: [
        "Order-to-fulfillment automation",
        "Abandoned-cart recovery flows",
        "Inventory & pricing sync",
      ],
    },
    {
      name: "B2B SaaS Companies",
      builds: [
        "Trial-to-paid lifecycle automation",
        "Stripe-to-CRM billing sync",
        "Churn-risk alerting",
      ],
    },
    {
      name: "Real Estate Teams",
      builds: [
        "Lead routing & instant follow-up",
        "Listing-to-CRM sync",
        "Transaction-stage automation",
      ],
    },
  ],
  industriesCtaCopy: "See your industry's workflows mapped end-to-end.",
  industriesCtaLabel: "Map My Workflows",

  vettingEyebrow: "How to Vet an n8n Architect",
  vettingHeadlineNavy: "The 6 Criteria That",
  vettingHeadlineBlue: "Actually Matter.",
  vettingSub:
    "Most teams hire on price and regret on reliability. Use this scorecard before you pay anyone to touch your workflow stack.",
  criteria: [
    {
      label: "Workflow Implementation",
      what: "Can they architect fault-tolerant, multi-branch flows — not just single linear chains?",
      score: "Full webhook + router + Error Trigger architecture",
    },
    {
      label: "Reliability Engineering",
      what: "Do they build error directives and retries, or just hope APIs never change?",
      score: "Error Trigger + retry backoff + Slack alerting",
    },
    {
      label: "Code-Level Capability",
      what: "Can they write custom JavaScript nodes, or are they limited to no-code?",
      score: "Custom JS nodes for complex transforms and validation",
    },
    {
      label: "Specialist Expertise",
      what: "Are they a workflow architect or a generalist wearing many hats?",
      score: "n8n specialist, not a task-taker",
    },
    {
      label: "Scope Transparency",
      what: "Do they tell you exactly what they will and won't build — before you pay?",
      score:
        "Written audit + approved architecture before any production touch",
    },
    {
      label: "Post-Launch Support",
      what: "Who monitors the workflows after launch — and is it documented?",
      score: "Retained monitoring with a documented learning loop",
    },
  ],
  vaComparison: [
    {
      capability: "Multi-Branch Routing",
      va: "Cannot build — chains single linear flows",
      architect: "Conditional routers that fork by payload and deal value",
    },
    {
      capability: "Error Handling",
      va: "None — failures go silent",
      architect: "Error Trigger + retry backoff + Slack alerts",
    },
    {
      capability: "Custom Code Nodes",
      va: "No-code only, limited logic",
      architect: "Custom JavaScript for complex transforms",
    },
    {
      capability: "Bi-Directional Sync",
      va: "One-way, manual copy-paste",
      architect: "Real-time mirrored sync with dedup logic",
    },
    {
      capability: "Engagement Model",
      va: "Task-taker waiting for instructions",
      architect: "Direct engineer who owns outcomes, no middlemen",
    },
    {
      capability: "Documentation",
      va: "None — the workflow lives in their head",
      architect: "Documented, structured, transferable from day one",
    },
  ],
  vettingCtaCopy: "Vet me against this checklist on a call.",
  vettingCtaLabel: "Vet Me on a Call",

  pricingEyebrow: "Pricing & Value",
  pricingHeadlineNavy: "n8n Architect vs. General VA vs.",
  pricingHeadlineBlue: "US Agency.",
  pricingIntro:
    "A US automation agency charges a premium retainer. A general VA charges less but can't architect. A senior n8n architect delivers the full workflow architecture at a fraction — scoped per project or retainer.",
  pricingIntroHighlight: "Book a free call for a custom quote.",
  pricingRows: [
    {
      capability: "Multi-branch routing",
      architect: "Custom, version-controlled",
      va: "Single linear flows only",
      agency: "Yes, at premium rate",
    },
    {
      capability: "Error handling & retries",
      architect: "Full Error Trigger stack",
      va: "None — silent failures",
      agency: "Yes, at premium",
    },
    {
      capability: "Custom code nodes",
      architect: "Custom JavaScript",
      va: "No-code only",
      agency: "Yes, project-based",
    },
    {
      capability: "Self-hosted deployment",
      architect: "Docker + monitoring",
      va: "Cloud only, no infra",
      agency: "Yes, at high cost",
    },
    {
      capability: "Engagement model",
      architect: "Direct engineer, no middlemen",
      va: "Task-taker, reactive",
      agency: "Account-managed team",
    },
    {
      capability: "Ongoing maintenance",
      architect: "Documented learning loop",
      va: "Ad hoc, reactive",
      agency: "Retainer required",
    },
  ],

  freeAuditBadge: "ZERO-RISK DISCOVERY SESSION",
  freeAuditHeadlinePre: "Before You Pay Anyone —",
  freeAuditHeadlineBlue: "Let Me Audit Your Workflows.",
  freeAuditDesc:
    "On a focused 30-minute working call, I'll map your current n8n workflows, uncover silent failures and brittle chains, and show you exactly what needs to be architected for production scale. If I'm not the best fit, I'll point you directly to who is.",
  freeAuditDeliverables: [
    {
      title: "Failure & Leak Map",
      desc: "Every workflow that fails silently, burns memory, or drops leads without an alert.",
      tag: "DIAGNOSTIC",
    },
    {
      title: "Workflow & Webhook Health Check",
      desc: "Code review of routing logic, Error Trigger directives, payload validation, and trigger efficiency.",
      tag: "CODE REVIEW",
    },
    {
      title: "90-Day Architecture Roadmap",
      desc: "Prioritized engineering plan: what to patch today, migrate next, and orchestrate at scale.",
      tag: "ACTION PLAN",
    },
  ],
  freeAuditCtaLabel: "Book the Free Workflow Audit",

  whoEyebrow: "Who I Am",
  whoHeadlineNavy: "A Systems Engineer,",
  whoHeadlineBlue: "Not a Task-Taker.",
  whoName: "Marjohn Robillo",
  whoRole: "n8n Workflow Architect",
  whoBio:
    "I came from disciplined environments where documentation wasn't optional. I bring that same standard to every n8n build — documented, structured, and transferable. 5+ years, 300+ workflow systems, four continents.",
  whoImage:
    "https://vibe.filesafe.space/1790273900671562476/assets/27f82611-60cc-4927-874a-9e93dbc30ea2.png",
  whoImageAlt: "Marjohn Robillo — n8n Workflow Architect",
  credentials: [
    "MPA Graduate",
    "Licensed Professional Teacher (LPT)",
    "MUN-UNESCO Diplomat · Bali 2022",
    "Licensed Financial Advisor · PRULife UK",
    "Former CO1 · Bureau of Corrections PH",
    "n8n & Workflow Automation Specialist",
  ],
  whoCtaCopy: "Book a call directly with me — no account manager in between.",
  whoCtaLabel: "Book a Call",

  casesEyebrow: "Related Architecture Guides",
  casesHeadlineNavy: "Architecture",
  casesHeadlineBlue: "Guides.",
  caseStudies: [
    {
      to: "/blog/n8n-workflow-developer-freelance",
      title:
        "How to Connect a Vapi Voice AI Call Node into a CRM Pipeline via n8n",
      desc: "Advanced API routing and webhook manipulation for AI-driven call workflows.",
    },
    {
      to: "/blog/crm-error-handling-and-fallbacks-developer",
      title: "Architecting Zero-Drop Error Handling in n8n",
      desc: "Never lose a lead to a silent API failure again — Error Trigger nodes and retry backoff.",
    },
    {
      to: "/blog/hire-n8n-automation-expert-philippines",
      title: "Hiring an n8n Automation Expert (Not a Task-Taker)",
      desc: "What to look for when you need workflow architecture, not single linear flows.",
    },
    {
      to: "/blog/n8n-webhook-engineering-consultant",
      title: "Webhook-First Engineering for Unlimited n8n Executions",
      desc: "Replace wasteful polling with instant triggers and zero task burn.",
    },
  ],

  faqEyebrow: "Frequently Asked",
  faqHeadlineNavy: "Questions Before",
  faqHeadlineBlue: "Hiring Me.",
  faqs: [
    {
      question: "What does an n8n workflow architect actually do?",
      answer:
        "An n8n workflow architect designs and builds the entire automation orchestration inside n8n: multi-branch routing, custom JavaScript code nodes, Error Trigger error handling, bi-directional CRM sync, webhook-first triggers, and execution monitoring. They engineer the infrastructure, not just configure it.",
    },
    {
      question: "Can n8n replace Zapier for enterprise automation?",
      answer:
        "Yes. n8n replaces fragile Zapier stacks with self-hosted, code-level workflow control, unlimited execution volume for a flat fee, custom JavaScript nodes, and production-grade error handling — ideal for agencies running high-volume or complex automations.",
    },
    {
      question: "How much does an n8n workflow architect cost?",
      answer:
        "Engagements are scoped per project or retainer. A senior n8n architect delivers the full workflow architecture at a fraction of a US agency rate. Book a free workflow audit call to receive a custom scope and quote for your n8n automation needs.",
    },
    {
      question: "What happens if a webhook fails at 3 AM?",
      answer:
        "Every workflow I build includes error handling: Error Trigger nodes with automatic retry and exponential backoff, fallback routes to secondary endpoints, and Slack/email alerts the moment a failure occurs. No silent failures — your team knows within 60 seconds.",
    },
    {
      question: "Can you fix an n8n setup another freelancer built?",
      answer:
        "Yes. I inherit and rebuild broken workflows with a documented audit first — mapping every node, webhook, and data flow before any changes, so nothing breaks during the transition.",
    },
    {
      question: "Do you build custom JavaScript code nodes in n8n?",
      answer:
        "Yes. When built-in nodes fall short, I deploy custom JavaScript inside any node for complex transforms, regex validation, and logic that no-code tools simply can't express.",
    },
    {
      question: "Can you connect AI agents and LLMs into n8n workflows?",
      answer:
        "Yes. I wire Vapi voice AI, OpenAI, and Claude into n8n workflows — transcribe calls, summarize notes, and auto-update deal health tags so reps spend time selling instead of writing notes.",
    },
    {
      question: "How do we get started without disrupting live operations?",
      answer:
        "Every engagement begins with an audit and staging blueprint. We test all workflows, webhooks, and sync logic in a sandbox environment before rollout — ensuring your active automations run 100% uninterrupted.",
    },
  ],
  faqCtaCopy: "Still deciding? Let's talk it through on a free call.",
  faqCtaLabel: "Book a Free Call",
};

// Keep the unused icon imports referenced so the bundler tree-shakes cleanly.
export const _n8nIcons = {
  Network,
  GitMerge,
  TrendingUp,
  PieChart,
  Webhook,
  Building,
  Boxes,
  Database,
  Repeat,
  Package,
  CalendarClock,
  Mail,
  Star,
  CreditCard,
  Plug,
  Layers,
  Zap,
  Cpu,
  DollarSign,
};
